import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import reviewsService from '../services/reviews'
import { useLocations } from './LocationsContext'

const ReviewsContext = createContext()

export const useReviews = () => {
  const context = useContext(ReviewsContext)
  if (!context) {
    throw new Error('useReviews must be used within a ReviewsProvider')
  }
  return context
}

export const ReviewsProvider = ({ children }) => {
  const { selectedLocationId } = useLocations()
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
  const [currentParams, setCurrentParams] = useState({
    location_id: null,
    page: 1,
    limit: 5,
    sort_by: 'review_date',
    sort_order: 'desc'
  })

  // Use refs to prevent concurrent fetches and store current values
  const reviewsRef = useRef([])
  const currentParamsRef = useRef({
    location_id: null,
    page: 1,
    limit: 5,
    sort_by: 'review_date',
    sort_order: 'desc'
  })
  const lastFetchRef = useRef(null)
  const fetchingRef = useRef(false)

  // Update refs when state changes
  reviewsRef.current = reviews
  currentParamsRef.current = currentParams
  lastFetchRef.current = lastFetch

  // Cache duration in milliseconds (2 minutes for reviews)
  const CACHE_DURATION = 2 * 60 * 1000

  const shouldRefetch = useCallback((params) => {
    if (fetchingRef.current) return false // Already fetching
    if (!lastFetchRef.current) return true
    
    // Check if params changed (excluding timestamp-based params)
    const { ...currentParamsForComparison } = currentParamsRef.current
    const { ...newParamsForComparison } = params
    
    if (JSON.stringify(newParamsForComparison) !== JSON.stringify(currentParamsForComparison)) return true
    
    // Check if cache expired
    if (Date.now() - lastFetchRef.current > CACHE_DURATION) return true
    
    return false
  }, [])

  const fetchReviews = useCallback(async (params = {}) => {
    // Use selectedLocationId from context if not provided
    const fetchParams = {
      location_id: selectedLocationId,
      page: 1,
      limit: 5,
      sort_by: 'review_date',
      sort_order: 'desc',
      ...currentParamsRef.current,
      ...params
    }

    if (!shouldRefetch(fetchParams)) {
      return reviewsRef.current
    }

    // Prevent concurrent fetches
    if (fetchingRef.current) {
      return reviewsRef.current
    }

    fetchingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const response = await reviewsService.getReviews(fetchParams)
      
      // Handle both array and object responses
      const reviewsData = Array.isArray(response) ? response : (response?.reviews || [])
      
      setReviews(reviewsData)
      setCurrentParams(fetchParams)
      setLastFetch(Date.now())
      return reviewsData
    } catch (err) {
      console.error('❌ Reviews fetch error:', err)
      setError(err.message || 'Failed to load reviews')
      setReviews([])
      throw err
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [shouldRefetch, selectedLocationId])

  const refreshReviews = useCallback(async (params = {}) => {
    setLastFetch(null) // Force refresh
    fetchingRef.current = false // Reset fetching flag
    return fetchReviews(params)
  }, [fetchReviews])

  const getRecentReviews = useCallback(async (limit = 5) => {
    return fetchReviews({
      limit,
      page: 1,
      sort_by: 'review_date',
      sort_order: 'desc'
    })
  }, [fetchReviews])

  const value = {
    reviews,
    loading,
    error,
    fetchReviews,
    refreshReviews,
    getRecentReviews,
    currentParams,
    lastFetch
  }

  return (
    <ReviewsContext.Provider value={value}>
      {children}
    </ReviewsContext.Provider>
  )
}

export default ReviewsContext